/**
 * The builder's palette: what an operator can drag onto a form.
 *
 * Written fresh rather than ported. the-answer-2's version hard-codes a
 * numeric id per preset (99, 98, 97…) that then has to be replaced on drop;
 * here a preset is a template and `makeField` mints the identity, which is the
 * only correct moment to do it.
 */

import {
  deriveFieldKey,
  newFieldId,
  uniqueFieldKey,
  type FieldType,
  type FormField,
} from "./types";

export interface FieldPreset {
  type: FieldType;
  label: string;
  /** What this captures, shown under the palette entry. */
  hint: string;
  options?: string[];
  group?: string;
}

/** Single fields, in palette order — commonest first. */
export const FIELD_PRESETS: FieldPreset[] = [
  { type: "text", label: "Single line", hint: "A name, a company, a claim number" },
  { type: "textarea", label: "Multi-line", hint: "The caller's message, in their words" },
  { type: "phone", label: "Phone", hint: "A callback number" },
  { type: "email", label: "Email", hint: "An email address" },
  { type: "extension", label: "Extension", hint: "A phone extension" },
  { type: "radio-group", label: "Choose one", hint: "One answer from a fixed list", options: ["Yes", "No"] },
  { type: "checkbox", label: "Yes / no", hint: "A single box the agent ticks" },
  { type: "date", label: "Date", hint: "An appointment or incident date" },
  { type: "time", label: "Time", hint: "A time of day" },
  { type: "date-time", label: "Date and time", hint: "Both together" },
  { type: "note", label: "Note to agent", hint: "Guidance. Captures nothing" },
  { type: "link", label: "Link", hint: "Something the agent opens on the call" },
];

/**
 * Groups of fields dropped together, because they are never useful alone.
 * They arrive sharing a section name so they render as a block.
 */
export const FIELD_PRESET_GROUPS: { label: string; hint: string; fields: FieldPreset[] }[] = [
  {
    label: "Address",
    hint: "Street through postcode, as one section",
    fields: [
      { type: "address-line-1", label: "Address line 1", hint: "", group: "Address" },
      { type: "address-line-2", label: "Address line 2", hint: "", group: "Address" },
      { type: "locality", label: "City", hint: "", group: "Address" },
      { type: "region", label: "State", hint: "", group: "Address" },
      { type: "postal-code", label: "ZIP code", hint: "", group: "Address" },
    ],
  },
  {
    label: "Caller",
    hint: "Who is calling and how to reach them",
    fields: [
      { type: "text", label: "First name", hint: "", group: "Caller" },
      { type: "text", label: "Last name", hint: "", group: "Caller" },
      { type: "phone", label: "Callback number", hint: "", group: "Caller" },
    ],
  },
];

/**
 * Mints a real field from a preset. `taken` is every key already on the form,
 * so a second "First name" becomes first_name_2 rather than a document the
 * server rejects.
 */
export function makeField(preset: FieldPreset, taken: Iterable<string>): FormField {
  const field: FormField = {
    id: newFieldId(),
    key: uniqueFieldKey(deriveFieldKey(preset.label), taken),
    type: preset.type,
    label: preset.label,
    deletable: true,
    editable: true,
  };
  if (preset.options) field.options = [...preset.options];
  if (preset.group) field.group = preset.group;
  if (preset.type === "note") field.note = "";
  return field;
}

/** Mints a whole preset group at once, keeping keys unique across the batch. */
export function makeFieldGroup(
  group: (typeof FIELD_PRESET_GROUPS)[number],
  taken: Iterable<string>,
): FormField[] {
  const keys = new Set(taken);
  return group.fields.map((preset) => {
    const field = makeField(preset, keys);
    keys.add(field.key);
    return field;
  });
}
